/**
 * DTD Pet Supplies — Theme JavaScript
 * Vanilla JS, no dependencies.
 * Shopify Online Store 2.0
 */

'use strict';

/* ============================================
   UTILITY HELPERS
   ============================================ */
const DTD = {
  /**
   * Debounce a function
   */
  debounce(fn, ms = 250) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), ms);
    };
  },

  /**
   * Throttle a function
   */
  throttle(fn, ms = 100) {
    let last = 0;
    return (...args) => {
      const now = Date.now();
      if (now - last >= ms) {
        last = now;
        return fn(...args);
      }
    };
  },

  /**
   * Format money (Shopify format helper)
   */
  formatMoney(cents, format = '${{amount}}') {
    const amount = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', amount);
  },

  /**
   * Emit a custom event
   */
  emit(eventName, detail = {}, target = document) {
    target.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true, composed: true }));
  },

  /**
   * Add event listener with cleanup return
   */
  on(el, event, handler, options) {
    if (!el) return () => {};
    el.addEventListener(event, handler, options);
    return () => el.removeEventListener(event, handler, options);
  },

  /**
   * Query elements
   */
  qs(selector, parent = document) {
    return parent.querySelector(selector);
  },

  qsAll(selector, parent = document) {
    return [...parent.querySelectorAll(selector)];
  },

  /**
   * Show toast notification
   */
  /**
   * Centralized overlay manager — prevents triple-listener race condition
   * when MobileMenu, SearchDrawer, and CartDrawer all share .site-overlay
   */
  _overlayCount: 0,

  showOverlay() {
    this._overlayCount++;
    const el = document.querySelector('.site-overlay');
    if (el) el.classList.add('is-visible');
  },

  hideOverlay() {
    this._overlayCount = Math.max(0, this._overlayCount - 1);
    if (this._overlayCount === 0) {
      const el = document.querySelector('.site-overlay');
      if (el) el.classList.remove('is-visible');
    }
  },

  forceHideOverlay() {
    this._overlayCount = 0;
    const el = document.querySelector('.site-overlay');
    if (el) el.classList.remove('is-visible');
  },

  toast(message, type = 'info', duration = 4000) {
    const container = document.querySelector('.toast-container') || (() => {
      const el = document.createElement('div');
      el.className = 'toast-container';
      document.body.appendChild(el);
      return el;
    })();

    const icons = {
      success: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color:#2D6A4F"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
      error: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color:#C0392B"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
      info: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="color:#C9893B"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
    };

    const toast = document.createElement('div');
    toast.className = `toast toast--${type}`;
    toast.innerHTML = `${icons[type] || icons.info}<span>${message}</span>`;
    container.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'fadeIn 0.3s ease reverse forwards';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }
};

/* ============================================
   ANNOUNCEMENT BAR
   ============================================ */
class AnnouncementBar {
  constructor(el) {
    this.el = el;
    this.items = DTD.qsAll('.announcement-bar__item', el);
    this.current = 0;
    this.interval = null;
    this.speed = parseInt(el.dataset.speed || '4000', 10);

    if (this.items.length <= 1) return;
    this.start();
  }

  show(index) {
    this.items.forEach(item => item.classList.remove('is-active'));
    this.items[index].classList.add('is-active');
    this.current = index;
  }

  next() {
    const nextIndex = (this.current + 1) % this.items.length;
    this.show(nextIndex);
  }

  start() {
    if (this.items.length === 0) return;
    this.show(0);
    this.interval = setInterval(() => this.next(), this.speed);
  }

  stop() {
    clearInterval(this.interval);
  }
}

/* ============================================
   STICKY HEADER
   ============================================ */
class StickyHeader {
  constructor(el) {
    this.el = el;
    this.bar = document.querySelector('.announcement-bar');
    this.lastScroll = 0;
    this.isSticky = false;
    this.isTransparent = el.dataset.transparent === 'true';
    this.threshold = 80;

    this.bindEvents();
    this.update();
  }

  update() {
    const scroll = window.scrollY;

    if (scroll > this.threshold) {
      if (!this.isSticky) {
        this.el.classList.add('is-sticky');
        this.isSticky = true;
        // Add body padding to prevent layout shift
        document.body.style.paddingTop = this.el.offsetHeight + 'px';
      }
      // Remove transparent when scrolled
      if (this.isTransparent) {
        this.el.classList.remove('is-transparent');
      }
    } else {
      if (this.isSticky) {
        this.el.classList.remove('is-sticky');
        this.isSticky = false;
        document.body.style.paddingTop = '0';
      }
      if (this.isTransparent) {
        this.el.classList.add('is-transparent');
      }
    }

    this.lastScroll = scroll;
  }

  bindEvents() {
    const handler = DTD.throttle(() => this.update(), 50);
    window.addEventListener('scroll', handler, { passive: true });
  }
}

/* ============================================
   MOBILE MENU
   ============================================ */
class MobileMenu {
  constructor() {
    this.hamburger = DTD.qs('.header__hamburger');
    this.nav = DTD.qs('.mobile-nav');
    this.closeBtn = DTD.qs('.mobile-nav__close');
    this.overlay = DTD.qs('.site-overlay');
    this.isOpen = false;

    if (!this.hamburger || !this.nav) return;
    this.bindEvents();
  }

  open() {
    this.nav.classList.add('is-open');
    this.hamburger.classList.add('is-open');
    this.hamburger.setAttribute('aria-expanded', 'true');
    DTD.showOverlay();
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
  }

  close() {
    this.nav.classList.remove('is-open');
    this.hamburger.classList.remove('is-open');
    this.hamburger.setAttribute('aria-expanded', 'false');
    DTD.hideOverlay();
    document.body.style.overflow = '';
    this.isOpen = false;
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  bindEvents() {
    this.hamburger.addEventListener('click', () => this.toggle());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    // Overlay click is handled by a single global listener — see init()
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }
}

/* ============================================
   SEARCH DRAWER
   ============================================ */
class SearchDrawer {
  constructor() {
    this.triggers = DTD.qsAll('[data-search-trigger]');
    this.drawer = DTD.qs('.search-drawer');
    this.closeBtn = DTD.qs('.search-drawer__close');
    this.input = DTD.qs('.search-drawer__input');
    this.overlay = DTD.qs('.site-overlay');
    this.isOpen = false;

    if (!this.drawer) return;
    this.bindEvents();
  }

  open() {
    this.drawer.classList.add('is-open');
    DTD.showOverlay();
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    setTimeout(() => this.input?.focus(), 300);
  }

  close() {
    this.drawer.classList.remove('is-open');
    DTD.hideOverlay();
    document.body.style.overflow = '';
    this.isOpen = false;
  }

  bindEvents() {
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', () => this.open());
    });
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    // Overlay click is handled by a single global listener — see init()
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }
}

/* ============================================
   SCROLL REVEAL
   ============================================ */
class ScrollReveal {
  constructor() {
    this.elements = DTD.qsAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    if (!this.elements.length) return;

    // Respect reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.elements.forEach(el => el.classList.add('is-visible'));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    this.elements.forEach(el => this.observer.observe(el));
  }
}

/* ============================================
   ACCORDION
   ============================================ */
class Accordion {
  constructor() {
    this.items = DTD.qsAll('.accordion-item');
    this.bindEvents();

    // Open items marked as default open
    this.items.forEach(item => {
      if (item.dataset.open === 'true') this.open(item);
    });
  }

  open(item) {
    const body = DTD.qs('.accordion-item__body', item);
    item.classList.add('is-open');
    if (body) {
      body.style.maxHeight = body.scrollHeight + 'px';
    }
  }

  close(item) {
    const body = DTD.qs('.accordion-item__body', item);
    item.classList.remove('is-open');
    if (body) body.style.maxHeight = '0';
  }

  toggle(item) {
    if (item.classList.contains('is-open')) {
      this.close(item);
    } else {
      this.open(item);
    }
  }

  bindEvents() {
    this.items.forEach(item => {
      const trigger = DTD.qs('.accordion-item__trigger', item);
      if (trigger) {
        trigger.addEventListener('click', () => this.toggle(item));
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggle(item);
          }
        });
      }
    });
  }
}

/* ============================================
   CART DRAWER
   ============================================ */
class CartDrawer {
  constructor() {
    this.drawer = DTD.qs('.cart-drawer');
    this.overlay = DTD.qs('.site-overlay');
    this.closeBtn = DTD.qs('.cart-drawer__close');
    this.itemsContainer = DTD.qs('.cart-drawer__items');
    this.subtotalEl = DTD.qs('.cart-drawer__subtotal-price');
    this.countEl = DTD.qs('.cart-drawer__count');
    this.shippingFill = DTD.qs('.shipping-progress__fill');
    this.shippingText = DTD.qs('.shipping-progress__text');
    this.freeShippingThreshold = parseFloat(
      document.querySelector('[data-free-shipping]')?.dataset.freeShipping || '4900'
    );
    this.isOpen = false;

    if (!this.drawer) return;
    this.bindEvents();
    this.updateHeaderCount();
  }

  open() {
    this.drawer.classList.add('is-open');
    DTD.showOverlay();
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    this.fetchCart();
  }

  close() {
    this.drawer.classList.remove('is-open');
    DTD.hideOverlay();
    document.body.style.overflow = '';
    this.isOpen = false;
  }

  async fetchCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.renderCart(cart);
      this.updateShippingProgress(cart.total_price);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  }

  renderCart(cart) {
    if (!this.itemsContainer) return;

    if (cart.item_count === 0) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer__empty">
          <svg class="cart-drawer__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 01-8 0"/>
          </svg>
          <p>Your cart is empty</p>
          <a href="/products/true-view-interactive-pet-camera-feeder" class="btn btn--primary btn--sm" onclick="if(window.cartDrawer)window.cartDrawer.close()">Shop Now</a>
        </div>
      `;
    } else {
      const trueViewImg = window.DTD_TRUE_VIEW_IMG || '';
      this.itemsContainer.innerHTML = cart.items.map(item => {
        const imgSrc = (trueViewImg && item.product_title && item.product_title.includes('True View'))
          ? trueViewImg
          : (item.image || '');
        return `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item__image">
            ${imgSrc ? `<img src="${imgSrc}" alt="${item.title}" loading="lazy" width="72" height="72" style="border-radius:8px;object-fit:cover;">` : ''}
          </div>
          <div class="cart-item__details">
            <div class="cart-item__title">${item.product_title}</div>
            ${item.variant_title ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
            <div class="cart-item__row">
              <div class="quantity-selector quantity-selector--sm">
                <button class="quantity-selector__btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
                <input class="quantity-selector__input" type="number" value="${item.quantity}" min="1" max="99" data-key="${item.key}" aria-label="Quantity" style="width:40px;height:36px;">
                <button class="quantity-selector__btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>
              <span class="cart-item__price">${DTD.formatMoney(item.line_price)}</span>
            </div>
            <button class="cart-item__remove" data-key="${item.key}">Remove</button>
          </div>
        </div>
      `;}).join('');

      // Bind quantity events
      this.bindCartItemEvents();
    }

    if (this.subtotalEl) {
      this.subtotalEl.textContent = DTD.formatMoney(cart.total_price);
    }

    if (this.countEl) {
      this.countEl.textContent = `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}`;
    }
  }

  updateShippingProgress(totalCents) {
    if (!this.shippingFill || !this.shippingText) return;
    const threshold = this.freeShippingThreshold;
    const percent = Math.min((totalCents / threshold) * 100, 100);
    this.shippingFill.style.width = percent + '%';

    if (totalCents >= threshold) {
      this.shippingText.innerHTML = `🎉 You've unlocked <strong>free shipping!</strong>`;
    } else {
      const remaining = DTD.formatMoney(threshold - totalCents);
      this.shippingText.innerHTML = `Add <strong>${remaining}</strong> more for free shipping`;
    }
  }

  async updateQuantity(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      this.renderCart(cart);
      this.updateShippingProgress(cart.total_price);
      this.updateHeaderCount(cart.item_count);
      DTD.emit('cart:updated', { cart });
    } catch (err) {
      console.error('Update quantity error:', err);
    }
  }

  async removeItem(key) {
    await this.updateQuantity(key, 0);
  }

  updateHeaderCount(count) {
    if (count === undefined) {
      fetch('/cart.js').then(r => r.json()).then(cart => {
        this.updateHeaderCount(cart.item_count);
      });
      return;
    }
    const countEl = DTD.qs('.header__cart-count');
    if (countEl) {
      countEl.textContent = count;
      countEl.style.display = count > 0 ? 'flex' : 'none';
    }
  }

  bindCartItemEvents() {
    // Quantity buttons
    DTD.qsAll('[data-action="increase"], [data-action="decrease"]', this.drawer).forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const input = this.drawer.querySelector(`.quantity-selector__input[data-key="${key}"]`);
        if (!input) return;
        const current = parseInt(input.value, 10);
        const next = btn.dataset.action === 'increase' ? current + 1 : Math.max(0, current - 1);
        input.value = next;
        this.updateQuantity(key, next);
      });
    });

    // Quantity input change
    DTD.qsAll('.quantity-selector__input', this.drawer).forEach(input => {
      input.addEventListener('change', () => {
        const key = input.dataset.key;
        const val = Math.max(0, parseInt(input.value, 10) || 0);
        this.updateQuantity(key, val);
      });
    });

    // Remove buttons
    DTD.qsAll('.cart-item__remove', this.drawer).forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        this.removeItem(key);
      });
    });
  }

  bindEvents() {
    // Open triggers
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-trigger]')) {
        e.preventDefault();
        this.open();
      }
    });

    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    // Overlay click is handled by a single global listener — see init()

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      if (e.detail?.cart) {
        this.updateHeaderCount(e.detail.cart.item_count);
      }
    });
  }
}

/* ============================================
   ADD TO CART (AJAX)
   ============================================ */
class AddToCart {
  constructor() {
    this.bindEvents();
  }

  async addItem(variantId, quantity = 1, properties = {}) {
    const body = { id: variantId, quantity, properties };

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Error adding to cart');
      }

      const item = await res.json();
      DTD.toast(`${item.product_title} added to cart!`, 'success');
      DTD.emit('cart:item-added', { item });

      // Fetch updated cart for header count
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      DTD.emit('cart:updated', { cart });

      // Open cart drawer
      if (window.cartDrawer) window.cartDrawer.open();

      return item;
    } catch (err) {
      DTD.toast(err.message, 'error');
      throw err;
    }
  }

  bindEvents() {
    document.addEventListener('submit', async (e) => {
      if (!e.target.matches('[data-product-form]')) return;
      e.preventDefault();

      const form = e.target;
      const btn = form.querySelector('[data-submit-btn]');
      const variantInput = form.querySelector('[name="id"]');

      if (!variantInput) return;

      const variantId = variantInput.value;
      const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);

      if (btn) {
        btn.disabled = true;
        btn.innerHTML = `<span class="btn-spinner"></span> Adding...`;
      }

      try {
        await this.addItem(variantId, quantity);
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.textContent = 'Add to Cart';
        }
      }
    });

    // Quick add buttons on product cards
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;
      await this.addItem(variantId, 1).catch(() => {});
      btn.disabled = false;
    });
  }
}

/* ============================================
   QUANTITY SELECTOR
   ============================================ */
class QuantitySelector {
  constructor() {
    this.bindEvents();
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.quantity-selector__btn');
      if (!btn) return;

      const selector = btn.closest('.quantity-selector');
      if (!selector) return;

      const input = selector.querySelector('.quantity-selector__input');
      if (!input) return;

      const current = parseInt(input.value, 10) || 1;
      const action = btn.dataset.action;
      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '999', 10);

      let next = action === 'increase' ? current + 1 : current - 1;
      next = Math.max(min, Math.min(max, next));

      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

/* ============================================
   STICKY ATC BAR
   ============================================ */
class StickyATCBar {
  constructor() {
    this.bar = DTD.qs('.sticky-atc');
    this.trigger = DTD.qs('.featured-product, [data-sticky-trigger]');

    if (!this.bar || !this.trigger) return;
    this.bindEvents();
  }

  show() { this.bar.classList.add('is-visible'); }
  hide() { this.bar.classList.remove('is-visible'); }

  bindEvents() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          this.show();
        } else {
          this.hide();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -100px 0px' }
    );

    observer.observe(this.trigger);
  }
}

/* ============================================
   TESTIMONIALS / GENERIC SLIDER
   ============================================ */
class Slider {
  constructor(el) {
    this.el = el;
    this.track = DTD.qs('.testimonials-slider__track', el);
    this.slides = DTD.qsAll('.testimonial-card', el);
    this.prevBtn = DTD.qs('.slider-btn--prev', el);
    this.nextBtn = DTD.qs('.slider-btn--next', el);
    this.dots = DTD.qsAll('.slider-dot', el);
    this.current = 0;
    this.perSlide = this.getSlidesPerView();
    this.autoplay = el.dataset.autoplay !== 'false';
    this.autoplayInterval = null;

    if (this.slides.length <= 1) return;
    this.init();
  }

  getSlidesPerView() {
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  }

  get totalSlides() {
    return Math.ceil(this.slides.length / this.getSlidesPerView());
  }

  goTo(index) {
    const perView = this.getSlidesPerView();
    const max = this.slides.length - perView;
    this.current = Math.max(0, Math.min(index, max));
    const offset = (100 / perView) * this.current;
    if (this.track) this.track.style.transform = `translateX(-${offset}%)`;
    this.updateDots();
    this.updateButtons();
  }

  updateDots() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.current);
    });
  }

  updateButtons() {
    if (this.prevBtn) this.prevBtn.disabled = this.current === 0;
    if (this.nextBtn) {
      this.nextBtn.disabled = this.current >= this.slides.length - this.getSlidesPerView();
    }
  }

  next() {
    const max = this.slides.length - this.getSlidesPerView();
    this.goTo(this.current < max ? this.current + 1 : 0);
  }

  prev() {
    const max = this.slides.length - this.getSlidesPerView();
    this.goTo(this.current > 0 ? this.current - 1 : max);
  }

  startAutoplay() {
    if (!this.autoplay) return;
    this.autoplayInterval = setInterval(() => this.next(), 5000);
  }

  stopAutoplay() {
    clearInterval(this.autoplayInterval);
  }

  init() {
    this.goTo(0);

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    // Touch/swipe
    let startX = 0;
    this.track?.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.track?.addEventListener('touchend', (e) => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? this.next() : this.prev();
      }
    }, { passive: true });

    // Pause on hover
    this.el.addEventListener('mouseenter', () => this.stopAutoplay());
    this.el.addEventListener('mouseleave', () => this.startAutoplay());

    // Handle resize
    window.addEventListener('resize', DTD.debounce(() => {
      this.goTo(0);
    }, 300));

    this.startAutoplay();
  }
}

/* ============================================
   COUNTDOWN TIMER
   ============================================ */
class CountdownTimer {
  constructor(el) {
    this.el = el;
    this.endDate = new Date(el.dataset.endDate);
    this.rolling = el.dataset.rolling === 'true';
    this.hoursEl = DTD.qs('[data-countdown="hours"]', el);
    this.minsEl = DTD.qs('[data-countdown="minutes"]', el);
    this.secsEl = DTD.qs('[data-countdown="seconds"]', el);
    this.daysEl = DTD.qs('[data-countdown="days"]', el);
    this.interval = null;

    if (isNaN(this.endDate.getTime())) {
      console.warn('Invalid countdown date:', el.dataset.endDate);
      return;
    }

    this.start();
  }

  pad(n) { return String(n).padStart(2, '0'); }

  update() {
    const now = new Date();
    let diff = Math.max(0, Math.floor((this.endDate - now) / 1000));

    if (diff === 0) {
      if (this.rolling) {
        this.endDate = new Date(Date.now() + 24 * 60 * 60 * 1000);
        diff = 24 * 60 * 60;
      } else {
        clearInterval(this.interval);
        return;
      }
    }

    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const mins = Math.floor((diff % 3600) / 60);
    const secs = diff % 60;

    if (this.daysEl) this.daysEl.textContent = this.pad(days);
    if (this.hoursEl) this.hoursEl.textContent = this.pad(hours);
    if (this.minsEl) this.minsEl.textContent = this.pad(mins);
    if (this.secsEl) this.secsEl.textContent = this.pad(secs);
  }

  start() {
    this.update();
    this.interval = setInterval(() => this.update(), 1000);
  }

  stop() {
    clearInterval(this.interval);
  }
}

/* ============================================
   MEDIA GALLERY (Product Page)
   ============================================ */
class MediaGallery {
  constructor(el) {
    this.el = el;
    this.mainImg = DTD.qs('.media-gallery__main img', el);
    this.thumbs = DTD.qsAll('.media-gallery__thumb', el);
    this.current = 0;
    this.lightboxEnabled = el.dataset.lightbox !== 'false';

    this.bindEvents();
  }

  setActive(thumb, index) {
    this.thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
    this.current = index;

    const src = thumb.querySelector('img')?.src;
    const srcset = thumb.querySelector('img')?.srcset;
    if (this.mainImg && src) {
      this.mainImg.src = src;
      if (srcset) this.mainImg.srcset = srcset;
    }
  }

  bindEvents() {
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.setActive(thumb, i));
    });

    // Keyboard navigation
    this.el.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });
  }

  prev() {
    const index = Math.max(0, this.current - 1);
    if (this.thumbs[index]) this.setActive(this.thumbs[index], index);
  }

  next() {
    const index = Math.min(this.thumbs.length - 1, this.current + 1);
    if (this.thumbs[index]) this.setActive(this.thumbs[index], index);
  }
}

/* ============================================
   VARIANT PICKER
   ============================================ */
class VariantPicker {
  constructor(el) {
    this.el = el;
    this.form = el.closest('form[data-product-form]');
    this.productData = JSON.parse(
      DTD.qs('[data-product-json]', document)?.textContent || '{}'
    );
    this.variants = this.productData.variants || [];
    this.options = this.productData.options || [];
    this.currentVariant = this.variants[0];

    this.bindEvents();
    this.updateUI();
  }

  getSelectedOptions() {
    const selected = {};
    DTD.qsAll('.variant-option.is-selected, .variant-swatch.is-selected', this.el).forEach(opt => {
      const group = opt.closest('[data-option-position]');
      if (group) selected[group.dataset.optionPosition] = opt.dataset.value;
    });
    return selected;
  }

  findVariant(selections) {
    return this.variants.find(variant => {
      return Object.entries(selections).every(([pos, val]) => {
        return variant[`option${pos}`] === val;
      });
    });
  }

  updateUI() {
    if (!this.currentVariant) return;

    // Update price
    const priceEl = DTD.qs('[data-variant-price]');
    const comparePriceEl = DTD.qs('[data-variant-compare-price]');

    if (priceEl) {
      priceEl.textContent = DTD.formatMoney(this.currentVariant.price);
    }

    if (comparePriceEl) {
      if (this.currentVariant.compare_at_price > this.currentVariant.price) {
        comparePriceEl.textContent = DTD.formatMoney(this.currentVariant.compare_at_price);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Update hidden variant ID
    const idInput = this.form?.querySelector('[name="id"]');
    if (idInput) idInput.value = this.currentVariant.id;

    // Update ATC button
    const atcBtn = this.form?.querySelector('[data-submit-btn]');
    if (atcBtn) {
      if (!this.currentVariant.available) {
        atcBtn.disabled = true;
        atcBtn.textContent = 'Sold Out';
      } else {
        atcBtn.disabled = false;
        atcBtn.textContent = 'Add to Cart';
      }
    }

    // Update sticky ATC
    const stickyPrice = DTD.qs('.sticky-atc__price');
    if (stickyPrice) stickyPrice.textContent = DTD.formatMoney(this.currentVariant.price);

    DTD.emit('variant:changed', { variant: this.currentVariant });
  }

  bindEvents() {
    this.el.addEventListener('click', (e) => {
      const option = e.target.closest('.variant-option, .variant-swatch');
      if (!option || option.classList.contains('is-unavailable')) return;

      const group = option.closest('[data-option-position]');
      if (!group) return;

      // Update selection in group
      DTD.qsAll('.variant-option, .variant-swatch', group).forEach(o => o.classList.remove('is-selected'));
      option.classList.add('is-selected');

      // Update label
      const labelSpan = DTD.qs('.variant-picker__label span', group);
      if (labelSpan) labelSpan.textContent = option.dataset.value;

      // Find matching variant
      const selections = this.getSelectedOptions();
      this.currentVariant = this.findVariant(selections) || this.currentVariant;
      this.updateUI();
    });

    // Select/dropdown variant
    this.el.addEventListener('change', (e) => {
      if (e.target.tagName === 'SELECT') {
        const selections = {};
        DTD.qsAll('select', this.el).forEach((sel, i) => {
          selections[i + 1] = sel.value;
        });
        this.currentVariant = this.findVariant(selections) || this.currentVariant;
        this.updateUI();
      }
    });
  }
}

/* ============================================
   ANIMATED NUMBER COUNTER
   ============================================ */
class NumberCounter {
  constructor(el) {
    this.el = el;
    this.target = parseFloat(el.dataset.target || el.textContent || '0');
    this.duration = parseInt(el.dataset.duration || '2000', 10);
    this.suffix = el.dataset.suffix || '';
    this.prefix = el.dataset.prefix || '';
    this.decimals = el.dataset.decimals ? parseInt(el.dataset.decimals, 10) : 0;
    this.started = false;

    this.initObserver();
  }

  count(start, end, duration) {
    const startTime = performance.now();

    const update = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + (end - start) * eased;

      this.el.textContent = this.prefix + current.toFixed(this.decimals) + this.suffix;

      if (progress < 1) requestAnimationFrame(update);
    };

    requestAnimationFrame(update);
  }

  initObserver() {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.started) {
        this.started = true;
        this.count(0, this.target, this.duration);
        observer.unobserve(this.el);
      }
    }, { threshold: 0.5 });

    observer.observe(this.el);
  }
}

/* ============================================
   IMAGE GALLERY LIGHTBOX
   ============================================ */
class ImageLightbox {
  constructor() {
    this.lightbox = DTD.qs('.lightbox');
    this.lightboxImg = DTD.qs('.lightbox__img');
    this.closeBtn = DTD.qs('.lightbox__close');

    if (!this.lightbox) return;
    this.bindEvents();
  }

  open(src, alt = '') {
    if (this.lightboxImg) {
      this.lightboxImg.src = src;
      this.lightboxImg.alt = alt;
    }
    this.lightbox.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.lightbox.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      const item = e.target.closest('.gallery-item[data-lightbox]');
      if (item) {
        const img = item.querySelector('img');
        if (img) this.open(img.src, img.alt);
      }
    });

    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox) this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.lightbox.classList.contains('is-open')) this.close();
    });
  }
}

/* ============================================
   SMOOTH SCROLL
   ============================================ */
function initSmoothScroll() {
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;

    const target = document.querySelector(link.hash);
    if (!target) return;

    e.preventDefault();
    const header = document.querySelector('.site-header');
    const offset = header ? header.offsetHeight + 16 : 80;

    window.scrollTo({
      top: target.offsetTop - offset,
      behavior: 'smooth'
    });
  });
}

/* ============================================
   EXIT INTENT POPUP
   ============================================ */
class ExitIntent {
  constructor() {
    this.triggered = false;
    this.popup = DTD.qs('[data-exit-popup]');
    if (!this.popup) return;
    this.bindEvents();
  }

  show() {
    if (this.triggered) return;
    this.triggered = true;
    this.popup.classList.add('is-visible');
  }

  close() {
    this.popup.classList.remove('is-visible');
  }

  bindEvents() {
    document.addEventListener('mouseleave', (e) => {
      if (e.clientY <= 0 && !this.triggered) {
        setTimeout(() => this.show(), 300);
      }
    });

    const closeBtn = DTD.qs('[data-exit-popup-close]', this.popup);
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  }
}

/* ============================================
   NEWSLETTER FORM
   ============================================ */
class NewsletterForm {
  constructor(el) {
    this.form = el;
    this.successMsg = DTD.qs('[data-newsletter-success]', el.closest('.newsletter__inner') || document);
    this.bindEvents();
  }

  bindEvents() {
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = this.form.querySelector('input[type="email"]')?.value;
      if (!email) return;

      const btn = this.form.querySelector('button[type="submit"]');
      if (btn) { btn.disabled = true; btn.textContent = 'Subscribing...'; }

      // Shopify contact form submission
      try {
        const formData = new FormData(this.form);
        await fetch(this.form.action || '/', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' }
        });

        if (this.successMsg) {
          this.form.style.display = 'none';
          this.successMsg.style.display = 'block';
        } else {
          DTD.toast('Thanks for subscribing! 🐾', 'success');
        }
      } catch (err) {
        DTD.toast('Something went wrong. Please try again.', 'error');
        if (btn) { btn.disabled = false; btn.textContent = 'Subscribe'; }
      }
    });
  }
}

/* ============================================
   ANNOUNCEMENT BAR CLOSE
   ============================================ */
function initAnnouncementClose() {
  const btn = DTD.qs('[data-announcement-close]');
  const bar = DTD.qs('.announcement-bar');
  if (!btn || !bar) return;

  btn.addEventListener('click', () => {
    bar.style.display = 'none';
    sessionStorage.setItem('dtd_bar_closed', '1');
  });

  // Restore closed state
  if (sessionStorage.getItem('dtd_bar_closed') === '1') {
    bar.style.display = 'none';
  }
}

/* ============================================
   FREE SHIPPING PROGRESS BAR
   ============================================ */
function updateShippingProgress(totalCents, threshold) {
  const bar = DTD.qs('.shipping-progress__fill');
  if (!bar) return;
  const pct = Math.min((totalCents / threshold) * 100, 100);
  bar.style.setProperty('--progress-width', pct + '%');
  bar.style.width = pct + '%';
}

/* ============================================
   HEADER SCROLL BEHAVIOR
   ============================================ */
function initHeaderScrollBehavior() {
  const header = DTD.qs('.site-header');
  const announcementBar = DTD.qs('.announcement-bar');
  if (!header) return;

  let lastScrollY = 0;
  let ticking = false;

  const update = () => {
    const currentScrollY = window.scrollY;
    const barHeight = announcementBar ? announcementBar.offsetHeight : 0;

    if (currentScrollY > barHeight + 80) {
      header.classList.add('is-scrolled');
    } else {
      header.classList.remove('is-scrolled');
    }

    lastScrollY = currentScrollY;
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });
}

/* ============================================
   COLLECTION SORT
   ============================================ */
function initCollectionSort() {
  const sortSelect = DTD.qs('[data-sort-by]');
  if (!sortSelect) return;

  sortSelect.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location.href = url.toString();
  });
}

/* ============================================
   INIT ALL COMPONENTS
   ============================================ */
function init() {
  // Announcement bar
  DTD.qsAll('.announcement-bar').forEach(el => new AnnouncementBar(el));

  // Sticky header
  const header = DTD.qs('.site-header');
  if (header) window.stickyHeader = new StickyHeader(header);

  // Mobile menu
  window.mobileMenu = new MobileMenu();

  // Search drawer
  window.searchDrawer = new SearchDrawer();

  // Scroll reveal
  new ScrollReveal();

  // Accordion
  new Accordion();

  // Cart drawer
  window.cartDrawer = new CartDrawer();

  // Add to cart
  new AddToCart();

  // Quantity selectors
  new QuantitySelector();

  // Sticky ATC
  new StickyATCBar();

  // Sliders
  DTD.qsAll('.testimonials-slider').forEach(el => new Slider(el));

  // Countdown timers
  DTD.qsAll('[data-countdown-timer]').forEach(el => new CountdownTimer(el));

  // Media galleries
  DTD.qsAll('.media-gallery').forEach(el => new MediaGallery(el));

  // Variant pickers
  DTD.qsAll('.variant-picker').forEach(el => new VariantPicker(el));

  // Number counters
  DTD.qsAll('[data-counter]').forEach(el => new NumberCounter(el));

  // Image lightbox
  new ImageLightbox();

  // Newsletter forms
  DTD.qsAll('[data-newsletter-form]').forEach(el => new NewsletterForm(el));

  // Smooth scroll
  initSmoothScroll();

  // Exit intent
  new ExitIntent();

  // Single global overlay click handler — closes whichever drawer is open
  const siteOverlay = DTD.qs('.site-overlay');
  if (siteOverlay) {
    siteOverlay.addEventListener('click', () => {
      if (window.cartDrawer?.isOpen) window.cartDrawer.close();
      else if (window.mobileMenu?.isOpen) window.mobileMenu.close();
      else if (window.searchDrawer?.isOpen) window.searchDrawer.close();
      else DTD.forceHideOverlay(); // safety valve
    });
  }

  // Announcement close
  initAnnouncementClose();

  // Collection sort
  initCollectionSort();

  // Header scroll
  initHeaderScrollBehavior();

  console.log('🐾 DTD Pet Supplies theme initialized');
}

/* ============================================
   DOM READY
   ============================================ */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

/* ============================================
   SHOPIFY SECTION EVENTS (for theme editor live reload)
   ============================================ */
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target;

  // Re-init components in reloaded section
  DTD.qsAll('.announcement-bar', section).forEach(el => new AnnouncementBar(el));
  DTD.qsAll('.testimonials-slider', section).forEach(el => new Slider(el));

  // Re-bind accordion triggers
  DTD.qsAll('.accordion-item__trigger', section).forEach(trigger => {
    const item = trigger.closest('.accordion-item');
    if (!item) return;
    trigger.addEventListener('click', () => {
      const isOpen = item.classList.contains('is-open');
      DTD.qsAll('.accordion-item', section).forEach(i => i.classList.remove('is-open'));
      if (!isOpen) item.classList.add('is-open');
    });
  });

  DTD.qsAll('[data-countdown-timer]', section).forEach(el => new CountdownTimer(el));
  DTD.qsAll('[data-counter]', section).forEach(el => new NumberCounter(el));

  new ScrollReveal();
});

document.addEventListener('shopify:section:unload', (e) => {
  // Allow any component teardown if needed in future
});

// Highlight the section being edited in the Theme Editor
document.addEventListener('shopify:section:select', (e) => {
  const section = e.target;
  if (section) section.classList.add('theme-editor--selected');
});

document.addEventListener('shopify:section:deselect', (e) => {
  const section = e.target;
  if (section) section.classList.remove('theme-editor--selected');
});

// Highlight the block being edited
document.addEventListener('shopify:block:select', (e) => {
  const block = document.querySelector('[data-shopify-editor-block]');
  if (block) block.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

document.addEventListener('shopify:block:deselect', () => {
  // Block deselected — nothing to clean up by default
});

/* ============================================
   EXPOSE GLOBALS FOR INLINE USE
   ============================================ */
window.DTD = DTD;
